import { CdkAccordionItem } from '@angular/cdk/accordion';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { Route, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { FlyoutCoordinatorService } from './flyout.coordinator.service';
import { SidenavPersistenceService } from './sidenav.persistence.service';

@Component({
    selector: 'inline-sidenav-group',
    templateUrl: 'inline.sidenav.group.component.html',
    styleUrls: ['inline.sidenav.group.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, RouterLink, RouterLinkActive, CdkOverlayOrigin, CdkConnectedOverlay],
    hostDirectives: [CdkAccordionItem]
})
export class InlineSidenavGroupComponent {

    private readonly accordionItem = inject(CdkAccordionItem);
    private readonly router = inject(Router);
    private readonly persistence = inject(SidenavPersistenceService);
    private readonly flyout = inject(FlyoutCoordinatorService);

    public readonly $route = input.required<Route>({ alias: 'route' });
    public readonly $expanded = input<boolean>(false, { alias: 'expanded' });

    protected readonly $childrenId = computed(() => `inline-nav-group-${this.$route().path}`);
    protected readonly $flyoutOpen = computed(() => this.flyout.$activeFlyoutId() === this.$route().path);

    private readonly $navigationEnd = toSignal(
        this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
        { initialValue: null }
    );

    protected readonly $hasActiveChild = computed(() => {
        this.$navigationEnd();
        const route = this.$route();
        const url = this.router.url;
        return (route.children ?? []).some(c => url.startsWith(`/${route.path}/${c.path}`));
    });

    protected readonly flyoutPositions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 0,
            offsetY: 0
        },
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetX: 0,
            offsetY: 0
        },
        {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 0,
            offsetY: 0
        }
    ];

    constructor() {

        effect(() => {

            const path = this.$route().path;
            const open = this.persistence.$openGroups().has(path);
            const forceClosed = this.persistence.$forceClosedGroups().has(path);
            const activeChild = this.$hasActiveChild();
            const shouldOpen = open || (activeChild && !forceClosed);
            shouldOpen ? this.accordionItem.open() : this.accordionItem.close();
        });
    }

    protected readonly $itemExpanded = toSignal(this.accordionItem.expandedChange, { initialValue: this.accordionItem.expanded });

    protected onToggleInline(): void {

        this.persistence.toggle(this.$route().path, this.$hasActiveChild());
    }

    protected onTriggerEnter(): void {

        this.flyout.open(this.$route().path);
    }

    protected onTriggerLeave(): void {

        this.flyout.scheduleClose();
    }

    protected onFlyoutEnter(): void {

        this.flyout.cancelClose();
    }

    protected onFlyoutLeave(): void {

        this.flyout.scheduleClose();
    }

    protected onChildClick(): void {

        this.flyout.closeAll();
    }
}
